'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { buildProjectLandingWorkspaceHref } from '../components/layout/workspaceRouting';
import { PROJECT_LANDING_FALLBACK_PATH, resolveProjectLandingPath } from '../lib/projectNavRoutes';
import { fetchProjectAccessCached, readProjectAccessCache } from './queries/projectAccessQuery';
import type { ProjectAccess } from '../lib/api/projectPermissions';

export type ProjectLandingNavigation = {
  /**
   * 프로젝트 진입 href.
   *
   * 그 프로젝트의 permission을 이미 알면 권한 기반 목적지, 아직 모르면 잠정 목적지(대시보드)다.
   * 잠정 목적지인 동안에는 `hasResolvedAccess`가 false이므로, 클릭 시 확정 경로로 대체해야 한다.
   */
  buildHref: (teamId: string, projectId: string) => string;
  /** permission을 이미 알고 있어 `buildHref` 결과가 확정 목적지인지. */
  hasResolvedAccess: (projectId: string) => boolean;
  /** hover/focus 시 permission을 미리 받아둔다 — 클릭 시점에는 목적지가 이미 확정돼 있다. */
  prefetch: (projectId: string) => void;
  /** 이동 직전 확정 경로. 조회에 실패하면 대시보드로 되돌린다(게이트가 안내를 맡는다). */
  resolveLandingPath: (projectId: string) => Promise<string>;
  resolveHref: (teamId: string, projectId: string) => Promise<string>;
};

const toLandingPath = (access: ProjectAccess): string =>
  resolveProjectLandingPath((permission) => access.permissions.includes(permission)) ?? PROJECT_LANDING_FALLBACK_PATH;

/**
 * 프로젝트 진입 목적지를 그 프로젝트의 permission으로 정한다.
 *
 * 항상 `/dashboard`로 보내면 `dashboard.read`가 없는 역할(문서 열람 전용 등)은 프로젝트를 고르는
 * 순간 접근 거부 화면을 만난다. **다른 프로젝트의 권한**이라 화면에 걸린 훅으로는 알 수 없으므로
 * 진입면마다 이 훅을 쓴다 — 한 진입면만 고치면 "모달로 들어가면 문서함, 홈에서 들어가면 접근 거부"라는
 * 비일관 동작이 생긴다.
 */
export const useProjectLandingNavigation = (token: string | null): ProjectLandingNavigation => {
  const queryClient = useQueryClient();
  // 쿼리 캐시는 반응형이 아니라서, 캐시가 채워져도 `<Link href>`는 다시 계산되지 않는다.
  // 확정된 경로만 여기에 담아 렌더가 그 변화를 보게 한다(조회 자체는 계속 쿼리 캐시가 맡는다).
  const [landingPathByProjectId, setLandingPathByProjectId] = useState<Record<string, string>>({});
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const rememberLandingPath = useCallback((projectId: string, landingPath: string) => {
    if (!isMountedRef.current) return;
    setLandingPathByProjectId((current) =>
      current[projectId] === landingPath ? current : { ...current, [projectId]: landingPath },
    );
  }, []);

  const hasResolvedAccess = useCallback(
    (projectId: string) => landingPathByProjectId[projectId] !== undefined,
    [landingPathByProjectId],
  );

  const buildHref = useCallback(
    (teamId: string, projectId: string) =>
      buildProjectLandingWorkspaceHref(landingPathByProjectId[projectId] ?? null, teamId, projectId),
    [landingPathByProjectId],
  );

  const prefetch = useCallback(
    (projectId: string) => {
      if (!token || projectId.length === 0 || landingPathByProjectId[projectId] !== undefined) return;

      const cached = readProjectAccessCache(queryClient, projectId);
      if (cached) {
        rememberLandingPath(projectId, toLandingPath(cached));
        return;
      }

      void fetchProjectAccessCached(queryClient, token, projectId)
        .then((access) => rememberLandingPath(projectId, toLandingPath(access)))
        .catch(() => {
          // 조회 실패는 잠정 목적지(대시보드)로 남겨둔다 — 클릭 시 다시 시도한다.
        });
    },
    [landingPathByProjectId, queryClient, rememberLandingPath, token],
  );

  const resolveLandingPath = useCallback(
    async (projectId: string) => {
      try {
        const landingPath = toLandingPath(await fetchProjectAccessCached(queryClient, token, projectId));
        rememberLandingPath(projectId, landingPath);
        return landingPath;
      } catch {
        return PROJECT_LANDING_FALLBACK_PATH;
      }
    },
    [queryClient, rememberLandingPath, token],
  );

  const resolveHref = useCallback(
    async (teamId: string, projectId: string) =>
      buildProjectLandingWorkspaceHref(await resolveLandingPath(projectId), teamId, projectId),
    [resolveLandingPath],
  );

  return { buildHref, hasResolvedAccess, prefetch, resolveLandingPath, resolveHref };
};
